import type { ReactNode } from "react"
import Spinner from "@/components/Spinner/Spinner"
import styles from "@/styles/components/TemplateTile.module.css"

/**
 * One tile in a row of mutually exclusive choices: a glyph and a label.
 *
 * A decisive one-press control, so it takes the spinner — unlike the `Stepper`
 * and the family checkboxes beside it, which are pressed in bursts. Pressing one
 * rewrites the whole config, and a second press while the first is out would
 * write a template over the template that is already landing.
 */

interface TemplateTileProps {
  label: string
  icon: ReactNode
  selected: boolean
  onSelect: () => void
  loading?: boolean
  /**
   * Locked because a SIBLING tile's write is out. Separate from `loading` so the
   * tile actually working keeps its colour while the rest of the row greys — two
   * templates landing in an unknown order would leave a config that is neither.
   */
  disabled?: boolean
}

export default function TemplateTile({
  label,
  icon,
  selected,
  onSelect,
  loading = false,
  disabled = false,
}: TemplateTileProps) {
  return (
    <button
      type="button"
      className={selected ? `${styles.tile} ${styles.selected}` : styles.tile}
      aria-pressed={selected}
      aria-busy={loading || undefined}
      disabled={loading || disabled}
      onClick={onSelect}
    >
      <span className={styles.icon} aria-hidden="true">
        {loading ? <Spinner /> : icon}
      </span>
      <span className={styles.label}>{label}</span>
    </button>
  )
}
